import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { t } from '../../i18n/nl.js';

const MEAL_TYPES = [
  { value: 'breakfast',       label: 'Ontbijt' },
  { value: 'snack_morning',   label: 'Tussendoor (ochtend)' },
  { value: 'lunch',           label: 'Lunch' },
  { value: 'snack_afternoon', label: 'Tussendoor (middag)' },
  { value: 'dinner',          label: 'Avondeten' },
  { value: 'evening_snack',   label: 'Avondsnack' },
];

const DINNER_COMPONENT_TYPES = [
  { value: 'protein',    label: 'Vlees/Vis' },
  { value: 'carbs',      label: 'Koolhydraten' },
  { value: 'vegetables', label: 'Groente' },
];

function mealTypeLabel(value) {
  return MEAL_TYPES.find(m => m.value === value)?.label ?? value;
}

function mealMomentsLabel(momentRows) {
  return (momentRows || []).map(m => mealTypeLabel(m.meal_moment)).join(', ');
}

function dinnerComponentLabel(value) {
  return DINNER_COMPONENT_TYPES.find(d => d.value === value)?.label ?? value;
}

function DeactiveerConfirm({ recept, onClose, onBevestigd }) {
  const [loading, setLoading] = useState(false);

  async function handleBevestig() {
    setLoading(true);
    await onBevestigd();
    setLoading(false);
  }

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 420 }}>
        <div className="modal-header">
          <h2 className="modal-title">{t('recipes.deactiverenTitel')}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <p style={{ marginBottom: 24, lineHeight: 1.6 }}>{t('recipes.deactiverenTekst')}</p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={onClose}>{t('recipes.annuleren')}</button>
          <button
            className="btn btn-primary"
            style={{ background: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}
            onClick={handleBevestig}
            disabled={loading}
          >
            {loading ? '…' : t('recipes.deactiverenBevestigen')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ReceptenPage() {
  const navigate = useNavigate();
  const [recepten, setRecepten] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [zoek, setZoek] = useState('');
  const [filterMealType, setFilterMealType] = useState('');
  const [toonInactief, setToonInactief] = useState(false);
  const [deactiveerTarget, setDeactiveerTarget] = useState(null);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await supabase
        .from('recipes')
        .select('id, name, calories, protein, carbs, fat, is_dinner_component, dinner_component_type, description, is_active, recipe_type, recipe_meal_moments(meal_moment)')
        .order('name');
      if (err) throw err;
      setRecepten(data || []);
    } catch (err) {
      setError(t('recipes.foutLaden') + err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleToggleActief(recept) {
    if (!recept.is_active) {
      // Activeren: direct doen zonder bevestiging
      try {
        const { data: updRow, error: err } = await supabase
          .from('recipes')
          .update({ is_active: true })
          .eq('id', recept.id)
          .select('id')
          .maybeSingle();
        if (err) throw err;
        if (!updRow) { setError(t('common.wijzigingNietOpgeslagen')); return; }
        setRecepten(prev => prev.map(r => r.id === recept.id ? { ...r, is_active: true } : r));
      } catch (err) {
        setError(t('recipes.foutOpslaan') + err.message);
      }
    } else {
      setDeactiveerTarget(recept);
    }
  }

  async function handleDeactiveerBevestigd() {
    try {
      const { data: updRow, error: err } = await supabase
        .from('recipes')
        .update({ is_active: false })
        .eq('id', deactiveerTarget.id)
        .select('id')
        .maybeSingle();
      if (err) throw err;
      if (!updRow) { setError(t('common.wijzigingNietOpgeslagen')); setDeactiveerTarget(null); return; }
      setRecepten(prev => prev.map(r => r.id === deactiveerTarget.id ? { ...r, is_active: false } : r));
      setDeactiveerTarget(null);
    } catch (err) {
      setError(t('recipes.foutOpslaan') + err.message);
      setDeactiveerTarget(null);
    }
  }

  const gefilterd = recepten.filter(r => {
    if (!toonInactief && !r.is_active) return false;
    if (filterMealType && !(r.recipe_meal_moments || []).some(m => m.meal_moment === filterMealType)) return false;
    if (zoek && !r.name.toLowerCase().includes(zoek.toLowerCase())) return false;
    return true;
  });

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">{t('recipes.title')}</h1>
          <p className="page-subtitle">{t('recipes.subtitle')}</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/coach/recepten/nieuw')}>
          {t('recipes.nieuw')}
        </button>
      </div>

      {error && <div className="error-msg" style={{ marginBottom: 20 }}>{error}</div>}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          className="form-input"
          style={{ maxWidth: 260 }}
          placeholder={t('recipes.zoekPlaceholder')}
          value={zoek}
          onChange={e => setZoek(e.target.value)}
        />
        <select
          className="form-input"
          style={{ maxWidth: 220 }}
          value={filterMealType}
          onChange={e => setFilterMealType(e.target.value)}
        >
          <option value="">{t('recipes.alleEetmomenten')}</option>
          {MEAL_TYPES.map(m => (
            <option key={m.value} value={m.value}>{m.label}</option>
          ))}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--color-text-muted)', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={toonInactief}
            onChange={e => setToonInactief(e.target.checked)}
            style={{ width: 16, height: 16, cursor: 'pointer' }}
          />
          {t('recipes.toonInactief')}
        </label>
      </div>

      {loading ? (
        <div className="loading-center"><div className="spinner" /></div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>{t('recipes.colNaam')}</th>
                  <th>{t('recipes.colEetmoment')}</th>
                  <th>{t('recipes.colKcal')}</th>
                  <th>{t('recipes.colEiwitten')}</th>
                  <th>{t('recipes.colKoolhydraten')}</th>
                  <th>{t('recipes.colVetten')}</th>
                  <th>{t('recipes.colAvondetenComponent')}</th>
                  <th>{t('recipes.colStatus')}</th>
                  <th style={{ textAlign: 'right' }}>{t('recipes.colActies')}</th>
                </tr>
              </thead>
              <tbody>
                {gefilterd.length === 0 ? (
                  <tr>
                    <td colSpan={9}>
                      <div className="empty-state">
                        <div style={{ fontSize: 32 }}>🥗</div>
                        <p>{t('recipes.geenRecepten')}</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  gefilterd.map(r => (
                    <tr key={r.id} style={!r.is_active ? { opacity: 0.45 } : {}}>
                      <td className="font-medium">
                        {r.name}
                        {r.recipe_type === 'uitgebreid' && (
                          <span className="badge badge-active" style={{ marginLeft: 8, fontSize: 10 }}>{t('recipes.badgeExtended')}</span>
                        )}
                      </td>
                      <td>{mealMomentsLabel(r.recipe_meal_moments)}</td>
                      <td>{r.calories}</td>
                      <td>{r.protein != null ? `${r.protein} g` : '—'}</td>
                      <td>{r.carbs   != null ? `${r.carbs} g`   : '—'}</td>
                      <td>{r.fat     != null ? `${r.fat} g`     : '—'}</td>
                      <td>
                        {r.is_dinner_component
                          ? dinnerComponentLabel(r.dinner_component_type)
                          : '—'}
                      </td>
                      <td>
                        <span className={`badge ${r.is_active ? 'badge-active' : 'badge-inactive'}`}>
                          {r.is_active ? t('recipes.badgeActief') : t('recipes.badgeInactief')}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/coach/recepten/${r.id}/bewerken`)}>
                            {t('recipes.bewerken')}
                          </button>
                          <button
                            className="btn btn-ghost btn-sm"
                            style={{ color: r.is_active ? 'var(--color-danger)' : 'var(--color-primary)' }}
                            onClick={() => handleToggleActief(r)}
                          >
                            {r.is_active ? t('recipes.deactiveren') : t('recipes.activeren')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {deactiveerTarget && (
        <DeactiveerConfirm
          recept={deactiveerTarget}
          onClose={() => setDeactiveerTarget(null)}
          onBevestigd={handleDeactiveerBevestigd}
        />
      )}
    </div>
  );
}
